// import node module libraries
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client/react";

import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import FormProvider from "components/general/hook-form/FormProvider";

import {
  Col,
  Row,
  Card,
  Form,
  Button,
  Image,
  Alert,
  InputGroup,
} from "react-bootstrap";

// import media files
import Logo from "assets/images/brand/logo/logo.svg";

import { PATH_AUTH } from "routes/Paths";

import { countries } from "utils/UtilData";
import moment from "moment";
import { validatePassword } from "helper/utils";
import { REGISTER_USER } from "helper/graphql/mutations";

const SignUp = () => {
  const navigate = useNavigate();

  const [profession, setProfesssion] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [pass, setPass] = useState('');

  const [onSignIn, { loading: isLoading }] = useMutation(REGISTER_USER);

  const SignInSchema = Yup.object().shape({
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    country: null,
    dateOfBirth: null,
    email: null,
    firstName: null,
    gender: null,
    isAdmin: false,
    isHealthcareWorker: false,
    isOrgAdmin: false,
    isPatient: false,
    lastName: null,
    password: null,
    phoneNumber: null,
    state: null,
    isConsentGiven: false,
    isPrivacyPolicyGiven: false,
    isFirstTimeLogin: true,
    isActive: true,
  };

  const methods = useForm({
    resolver: yupResolver(SignInSchema),
    defaultValues,
  });

  const {
    watch,
    setError,
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const handleSignIn = (data) => {
    const payload = data;
    payload.phoneNumber = `${data.code}${data.phoneNumber}`;
    delete payload.code

    if (profession === "healthWorker") {
      payload.isHealthcareWorker = true;
    } else {
      payload.isPatient = true;
    }

    onSignIn({
      variables: payload,
      onCompleted: (data) => {
        navigate(PATH_AUTH.login);
      },
      onError: (err) => {
        setError("afterSubmit", { ...err, message: err.message });
      },
    });
  };

  const showPassHandler = () => setShowPass(prev => !prev);

  return (
    <Fragment>
      <div className="auth-wrapper">
        <Row className="align-items-center justify-content-center g-0 min-vh-100">
          <Col xxl={7} xl={8} lg={9} md={11} className="py-8 py-xl-0">
            <Card className="auth-card">
              <Card.Body className="p-5 p-lg-6">
                {/* Logo & Header */}
                <div className="auth-header mb-5">
                  <Link to="/" className="logo-link">
                    <Image src={Logo} className="auth-logo" alt="Logo" />
                  </Link>
                  <h1 className="auth-title">Create Your Account</h1>
                  <p className="auth-subtitle">
                    Join us today and start your learning journey
                  </p>
                </div>

                {/* Error Alert */}
                {!!errors.afterSubmit && (
                  <Alert variant="danger" className="modern-alert">
                    <i className="fe fe-alert-circle me-2"></i>
                    {errors.afterSubmit.message}
                  </Alert>
                )}

                {/* Form */}
                <FormProvider
                  methods={methods}
                  onSubmit={handleSubmit(handleSignIn)}
                >
                  {/* Personal Information */}
                  <div className="form-section mb-5">
                    <div className="section-header mb-4">
                      <h5 className="section-title">
                        <i className="fe fe-user me-2"></i>
                        Personal Information
                      </h5>
                    </div>
                    <Row>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          First Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="firstName"
                          placeholder="Enter first name"
                          {...register("firstName")}
                          className="modern-input"
                          required
                        />
                      </Col>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          Last Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="lastName"
                          placeholder="Enter last name"
                          {...register("lastName")}
                          className="modern-input"
                          required
                        />
                      </Col>
                      <Col lg={12} className="mb-4">
                        <Form.Label className="modern-label">
                          Email Address <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="input-icon-wrapper">
                          <i className="fe fe-mail input-icon"></i>
                          <Form.Control
                            type="email"
                            id="email"
                            {...register("email")}
                            placeholder="Enter your email"
                            className="modern-input"
                            required
                          />
                        </div>
                      </Col>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          Gender <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select 
                          id="gender" 
                          {...register("gender")} 
                          className="modern-select"
                          required
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </Form.Select>
                      </Col>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          Date of Birth
                        </Form.Label>
                        <Form.Control
                          type="date"
                          id="dob"
                          max={moment().subtract(1, 'year').format('yyyy-MM-DD')}
                          {...register("dateOfBirth")}
                          className="modern-input"
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* Contact & Location */}
                  <div className="form-section mb-5">
                    <div className="section-header mb-4">
                      <h5 className="section-title">
                        <i className="fe fe-map-pin me-2"></i>
                        Contact & Location
                      </h5>
                    </div>
                    <Row>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          Country <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select 
                          id="country" 
                          {...register("country")}
                          className="modern-select"
                          required
                        >
                          <option value="">Select country</option>
                          {countries.map((item) => (
                            <option key={item.code} value={item.label}>
                              {item.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          State/County <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="state"
                          placeholder="Enter state/county"
                          {...register("state")}
                          className="modern-input"
                          required
                        />
                      </Col>
                      <Col lg={12} className="mb-4">
                        <Form.Label className="modern-label">
                          Phone Number
                        </Form.Label>
                        <div className="phone-input-group">
                          <Form.Select 
                            id="code" 
                            {...register("code")} 
                            className="modern-select phone-code"
                          >
                            <option value="">Code</option>
                            {countries.map((item) => (
                              <option key={item.code} value={item.phone}>
                                +{item.phone}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control
                            type="text"
                            id="phoneNumber"
                            placeholder="Enter phone number"
                            {...register("phoneNumber")}
                            className="modern-input phone-number"
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Professional Information */}
                  <div className="form-section mb-5">
                    <div className="section-header mb-4">
                      <h5 className="section-title">
                        <i className="fe fe-briefcase me-2"></i>
                        Professional Information
                      </h5>
                    </div>
                    <Row>
                      <Col lg={12} className="mb-4">
                        <Form.Label className="modern-label">
                          Profession <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          id="profession"
                          onChange={(e) => setProfesssion(e.target.value)}
                          className="modern-select"
                          required
                        >
                          <option value="">Select profession</option>
                          <option value="healthWorker">Medical/Health Worker</option>
                          <option value="other">Patient/Parent/Guardian</option>
                        </Form.Select>
                      </Col>
                    </Row>
                  </div>

                  {/* Security */}
                  <div className="form-section mb-5">
                    <div className="section-header mb-4">
                      <h5 className="section-title">
                        <i className="fe fe-lock me-2"></i>
                        Security
                      </h5>
                    </div>
                    <Row>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          Password <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup className="modern-input-group">
                          <div className="input-icon-wrapper flex-grow-1">
                            <i className="fe fe-lock input-icon"></i>
                            <Form.Control
                              type={showPass ? "text" : "password"}
                              id="pass"
                              value={pass}
                              placeholder="Create password"
                              className="modern-input with-toggle"
                              required
                              onChange={(e) => setPass(e.target.value)}
                              isInvalid={pass && !validatePassword(pass)}
                              isValid={pass && validatePassword(pass)}
                            />
                          </div>
                          <Button 
                            variant=""
                            className="password-toggle"
                            onClick={showPassHandler}
                            type="button"
                          >
                            <i className={showPass ? 'fe fe-eye-off' : 'fe fe-eye'}></i>
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            Password must be at least 8 characters
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Col>
                      <Col lg={6} md={6} className="mb-4">
                        <Form.Label className="modern-label">
                          Confirm Password <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup className="modern-input-group">
                          <div className="input-icon-wrapper flex-grow-1">
                            <i className="fe fe-check-circle input-icon"></i>
                            <Form.Control
                              type={showPass ? "text" : "password"}
                              id="password"
                              {...register("password")}
                              placeholder="Confirm password"
                              className="modern-input with-toggle"
                              required
                              isInvalid={watch().password && !(watch().password === pass)}
                              isValid={watch().password && (watch().password === pass)}
                            />
                          </div>
                          <Button 
                            variant=""
                            className="password-toggle"
                            onClick={showPassHandler}
                            type="button"
                          >
                            <i className={showPass ? 'fe fe-eye-off' : 'fe fe-eye'}></i>
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            Passwords do not match
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Col>
                    </Row>
                  </div>

                  {/* Terms & Conditions */}
                  <div className="form-section mb-5">
                    <div className="consent-box">
                      <Form.Check type="checkbox" id="privacy-check" className="modern-checkbox mb-3">
                        <Form.Check.Input
                          type="checkbox"
                          {...register("isPrivacyPolicyGiven")}
                          required
                        />
                        <Form.Check.Label>
                          I agree to the{" "}
                          <Link to="/pages/terms-and-conditions" target="_blank">
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link to="/pages/terms-and-conditions" target="_blank">
                            Privacy Policy
                          </Link>
                        </Form.Check.Label>
                      </Form.Check>

                      <Form.Check type="checkbox" id="consent-check" className="modern-checkbox">
                        <Form.Check.Input
                          type="checkbox"
                          {...register("isConsentGiven")}
                          required
                        />
                        <Form.Check.Label>
                          I consent to the collection, processing, and storage of my personal data by Hope Arthritis Foundation as specified, in compliance with the Data Protection Act, 2019.
                        </Form.Check.Label>
                      </Form.Check>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Row>
                    <Col xs={12} className="mb-4">
                      <Button
                        variant=""
                        type="submit"
                        disabled={isLoading || !watch("isPrivacyPolicyGiven") || !watch("isConsentGiven")}
                        className="auth-submit-btn w-100"
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating account...
                          </>
                        ) : (
                          <>
                            <i className="fe fe-user-plus me-2"></i>
                            Create Account
                          </>
                        )}
                      </Button>
                    </Col>

                    <Col xs={12}>
                      <p className="auth-footer-text">
                        Already have an account?{" "}
                        <Link to="/authentication/sign-in" className="auth-link">
                          Sign in here
                        </Link>
                      </p>
                    </Col>
                  </Row>
                </FormProvider>
              </Card.Body>
            </Card>

            {/* Footer */}
            <div className="auth-page-footer">
              <p className="mb-0">
                © {new Date().getFullYear()} Hope Arthritis Foundation. All rights reserved.
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <style jsx global>{`
        /* Auth Wrapper */
        .auth-wrapper {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
          position: relative;
        }

        .auth-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23F68521' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.5;
          pointer-events: none;
        }

        /* Auth Card */
        .auth-card {
          border: none;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .auth-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #F68521 0%, #1d9eb0 100%);
        }

        /* Auth Header */
        .auth-header {
          text-align: center;
        }

        .logo-link {
          display: inline-block;
          margin-bottom: 2rem;
        }

        .auth-logo {
          height: 48px;
          transition: transform 0.3s ease;
        }

        .logo-link:hover .auth-logo {
          transform: scale(1.05);
        }

        .auth-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .auth-subtitle {
          font-size: 1rem;
          color: #64748b;
          margin: 0;
        }

        /* Form Sections */
        .form-section {
          position: relative;
        }

        .section-header {
          padding-bottom: 1rem;
          border-bottom: 2px solid #f1f5f9;
        }

        .section-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
          display: flex;
          align-items: center;
        }

        .section-title i {
          color: #F68521;
        }

        /* Modern Alert */
        .modern-alert {
          border: none;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
        }

        /* Form Elements */
        .modern-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .input-icon-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 1.125rem;
          z-index: 5;
        }

        .modern-input {
          height: 52px;
          padding: 0 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .input-icon-wrapper .modern-input {
          padding-left: 3rem;
        }

        .modern-input:focus {
          border-color: #F68521;
          box-shadow: 0 0 0 4px rgba(246, 133, 33, 0.1);
        }

        .modern-input::placeholder {
          color: #cbd5e1;
        }

        .modern-select {
          height: 52px;
          padding: 0 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .modern-select:focus {
          border-color: #F68521;
          box-shadow: 0 0 0 4px rgba(246, 133, 33, 0.1);
        }

        /* Phone Input Group */
        .phone-input-group {
          display: flex;
          gap: 0.5rem;
        }

        .phone-code {
          width: 120px;
          flex-shrink: 0;
        }

        .phone-number {
          flex: 1;
        }

        /* Input Group for Password */
        .modern-input-group {
          display: flex;
          gap: 0.5rem;
        }

        .modern-input.with-toggle {
          border-radius: 12px;
        }

        .password-toggle {
          min-width: 52px;
          height: 52px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: white;
          color: #64748b;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-toggle:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #F68521;
        }

        .password-toggle i {
          font-size: 1.125rem;
        }

        /* Consent Box */
        .consent-box {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 1.5rem;
        }

        /* Checkbox */
        .modern-checkbox {
          font-size: 0.875rem;
          color: #475569;
          line-height: 1.6;
        }

        .modern-checkbox .form-check-input {
          width: 1.125rem;
          height: 1.125rem;
          border: 2px solid #cbd5e1;
          border-radius: 6px;
          margin-top: 0.125rem;
        }

        .modern-checkbox .form-check-input:checked {
          background-color: #F68521;
          border-color: #F68521;
        }

        .modern-checkbox a {
          color: #F68521;
          font-weight: 600;
          text-decoration: none;
        }

        .modern-checkbox a:hover {
          color: #1d9eb0;
          text-decoration: underline;
        }

        /* Submit Button */
        .auth-submit-btn {
          height: 52px;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 700;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.3);
        }

        .auth-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(246, 133, 33, 0.4);
          color:white;
        }

        .auth-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Footer Text */
        .auth-footer-text {
          text-align: center;
          font-size: 0.9375rem;
          color: #64748b;
          margin: 0;
        }

        .auth-link {
          color: #F68521;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .auth-link:hover {
          color: #1d9eb0;
        }

        /* Page Footer */
        .auth-page-footer {
          text-align: center;
          padding: 2rem 0;
          color: #94a3b8;
          font-size: 0.875rem;
        }

        /* Validation States */
        .is-invalid {
          border-color: #ef4444 !important;
        }

        .is-valid {
          border-color: #10b981 !important;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .auth-title {
            font-size: 1.75rem;
          }

          .auth-card {
            border-radius: 16px;
          }

          .auth-logo {
            height: 40px;
          }

          .section-title {
            font-size: 1rem;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default SignUp;